import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent } from "react";

export interface CustomSelectOption<T extends string | number = string> {
  value: T;
  label: string;
  hint?: string;
  disabled?: boolean;
}

interface Props<T extends string | number = string> {
  value: T | "";
  onChange: (value: T) => void;
  options: ReadonlyArray<CustomSelectOption<T>>;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  "aria-label"?: string;
  /** Dirección del popover (default down — auto-flip si no cabe abajo) */
  align?: "left" | "right";
  /** Maxheight del listbox (default 280px) */
  maxHeight?: number;
  /** Tamaño visual */
  size?: "md" | "sm";
}

/**
 * Select custom con estilo v2 — reemplaza al `<select>` nativo.
 *
 * - Accesible: roles ARIA combobox + listbox, teclado (↑↓ Home End Enter Esc).
 * - Click-outside cierra el popover.
 * - Si el listbox no cabe debajo, flipea hacia arriba.
 * - Animación suave de apertura.
 */
export function CustomSelect<T extends string | number = string>({
  value,
  onChange,
  options,
  placeholder = "Selecciona…",
  disabled = false,
  required = false,
  name,
  id,
  align = "left",
  maxHeight = 280,
  size = "md",
  ...aria
}: Props<T>) {
  const reactId = useId();
  const buttonId = id ?? `cs-${reactId}`;
  const listboxId = `${buttonId}-list`;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [flipUp, setFlipUp] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  const valueIndex = useMemo(
    () => options.findIndex((o) => o.value === value),
    [options, value],
  );

  // Close on outside click + escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Decide if popover flips up or down based on viewport space
  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setFlipUp(spaceBelow < maxHeight + 24 && rect.top > maxHeight);
  }, [open, maxHeight]);

  // When opening, focus first enabled option (selected if any)
  useEffect(() => {
    if (open) {
      setActiveIndex(valueIndex >= 0 ? valueIndex : firstEnabledIndex(options));
    }
  }, [open, valueIndex, options]);

  const handleSelect = useCallback(
    (idx: number) => {
      const opt = options[idx];
      if (!opt || opt.disabled) return;
      onChange(opt.value);
      setOpen(false);
      buttonRef.current?.focus();
    },
    [options, onChange],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!open) {
      if (
        e.key === "Enter" ||
        e.key === " " ||
        e.key === "ArrowDown" ||
        e.key === "ArrowUp"
      ) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => nextEnabled(options, i, +1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => nextEnabled(options, i, -1));
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(firstEnabledIndex(options));
        break;
      case "End":
        e.preventDefault();
        setActiveIndex(lastEnabledIndex(options));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (activeIndex >= 0) handleSelect(activeIndex);
        break;
      case "Tab":
        setOpen(false);
        break;
    }
  };

  return (
    <div
      className={`cs ${size === "sm" ? "cs-sm" : ""} ${disabled ? "cs-disabled" : ""}`}
      ref={containerRef}
    >
      {name && (
        <input
          type="hidden"
          name={name}
          value={value === "" ? "" : String(value)}
          required={required}
        />
      )}
      <button
        type="button"
        id={buttonId}
        ref={buttonRef}
        className="cs-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={aria["aria-label"]}
      >
        <span className={selected ? "cs-value" : "cs-placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`cs-caret ${open ? "open" : ""}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <ul
          id={listboxId}
          role="listbox"
          ref={listRef}
          className={`cs-list ${flipUp ? "cs-list-up" : ""} cs-list-${align}`}
          style={{ maxHeight }}
          tabIndex={-1}
        >
          {options.map((opt, i) => {
            const isActive = i === activeIndex;
            const isSelected = opt.value === value;
            return (
              <li
                key={String(opt.value)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled}
                className={`cs-option${isActive ? " active" : ""}${isSelected ? " selected" : ""}${opt.disabled ? " disabled" : ""}`}
                onMouseEnter={() => !opt.disabled && setActiveIndex(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(i)}
              >
                <span className="cs-option-label">{opt.label}</span>
                {opt.hint && <span className="cs-option-hint">{opt.hint}</span>}
                {isSelected && (
                  <svg
                    className="cs-check"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function firstEnabledIndex(opts: ReadonlyArray<{ disabled?: boolean }>): number {
  for (let i = 0; i < opts.length; i++) {
    if (!opts[i]?.disabled) return i;
  }
  return -1;
}

function lastEnabledIndex(opts: ReadonlyArray<{ disabled?: boolean }>): number {
  for (let i = opts.length - 1; i >= 0; i--) {
    if (!opts[i]?.disabled) return i;
  }
  return -1;
}

function nextEnabled(
  opts: ReadonlyArray<{ disabled?: boolean }>,
  from: number,
  dir: 1 | -1,
): number {
  const n = opts.length;
  if (n === 0) return -1;
  let i = from;
  for (let k = 0; k < n; k++) {
    i = (i + dir + n) % n;
    if (!opts[i]?.disabled) return i;
  }
  return from;
}
